import { ethers } from "ethers";
import {
  CONTRACT_ABI as STABLETRUST_CONTRACT_ABI,
  emptyPredicateAttestation,
  injectPredicateAttestation,
} from "@fairblock/stabletrust";

// SDK 2.0.15+ write methods take a Predicate attestation after the business
// args. The SDK client wraps its own contract; app-built ethers v5 contracts
// must inject the empty attestation (enforcement is off) the same way.
export {
  emptyPredicateAttestation,
  injectPredicateAttestation,
};

/** Pending action kinds (bytes32 keccak256 labels, matching facet storage constants). */
export const PENDING_KIND_CREATE = ethers.utils.id("CREATE");
export const PENDING_KIND_DEPOSIT = ethers.utils.id("DEPOSIT");
export const PENDING_KIND_TRANSFER = ethers.utils.id("TRANSFER");
export const PENDING_KIND_APPLY = ethers.utils.id("APPLY");
export const PENDING_KIND_WITHDRAW = ethers.utils.id("WITHDRAW");
export const PENDING_KIND_NONE =
  "0x0000000000000000000000000000000000000000000000000000000000000000";

/**
 * Contract ABI from @fairblock/stabletrust, plus app-only reads the SDK
 * does not ship (`getPendingAction`).
 */
export const CONTRACT_ABI = [
  ...STABLETRUST_CONTRACT_ABI,
  "function getPendingAction(address ownerAddr) external view returns ((bytes32,address,uint256,address,address,uint256,uint256,bytes))",
];

export function normalizePendingKind(kindRaw) {
  if (kindRaw == null) return null;
  if (typeof kindRaw === "string") return kindRaw.toLowerCase();
  try {
    return ethers.utils.hexlify(kindRaw).toLowerCase();
  } catch {
    return null;
  }
}

export function isPendingKind(kind, expected) {
  const k = normalizePendingKind(kind);
  const e = normalizePendingKind(expected);
  return !!k && !!e && k === e;
}

export function isEmptyPendingKind(kind) {
  const k = normalizePendingKind(kind);
  return !k || k === PENDING_KIND_NONE;
}

export function getPendingActionTypeName(kind) {
  const k = normalizePendingKind(kind);
  const names = {
    [PENDING_KIND_CREATE.toLowerCase()]: "account creation",
    [PENDING_KIND_DEPOSIT.toLowerCase()]: "deposit",
    [PENDING_KIND_TRANSFER.toLowerCase()]: "transfer",
    [PENDING_KIND_APPLY.toLowerCase()]: "apply pending balances",
    [PENDING_KIND_WITHDRAW.toLowerCase()]: "withdrawal",
  };
  return names[k] || "request";
}

export function getPendingProgressMessage(kind) {
  if (isPendingKind(kind, PENDING_KIND_DEPOSIT)) return "Deposit in progress...";
  if (isPendingKind(kind, PENDING_KIND_TRANSFER)) return "Transfer in progress...";
  if (isPendingKind(kind, PENDING_KIND_APPLY)) return "Secure claim in progress...";
  if (isPendingKind(kind, PENDING_KIND_WITHDRAW)) return "Withdraw in progress...";
  return null;
}

export function parseAccountCore(tuple) {
  if (!tuple) return null;
  return {
    exists: tuple[0],
    finalized: tuple[1],
    pendingAction: tuple[2],
    txId: tuple[3]?.toString?.() || "0",
    elgamalPubkey: tuple[4],
    pendingCreditCounter: tuple[5]?.toString?.() || "0",
  };
}

export function parsePendingKind(kindRaw) {
  const kind = normalizePendingKind(kindRaw);
  return isEmptyPendingKind(kind) ? null : kind;
}

/** Tempo (42431) defaults to off-chain ZKP / IPFS proofs, matching the SDK. */
export function shouldUseOffchainZKP(chainIdDecimal, options = {}) {
  if (options.offchainZKP !== undefined) return Boolean(options.offchainZKP);
  if (options.useOffchainVerify !== undefined) {
    return Boolean(options.useOffchainVerify);
  }
  return Number(chainIdDecimal) === 42431;
}
